/**
 * =============================================================================
 * ActivitiesBulkEditBar
 * Versão: 1.1.0 (2026-07-25)
 *
 * CHANGELOG:
 *   v1.1.0 (2026-07-25) - Adicionado botão "Editar" para paridade com o ícone
 *                         de lápis da coluna Ações. Só aparece quando há
 *                         exatamente 1 atividade selecionada.
 *   v1.0.0 (2026-05-10) - Versão inicial.
 *
 * Barra de edição em massa para a listagem de Atividades.
 * =============================================================================
 */
import { useState } from 'react';
import {
  CheckCircle2,
  CalendarClock,
  UserPlus,
  SkipForward,
  Trash2,
  Pencil,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';

// ===========================================================================
// Tipos
// ===========================================================================
interface ActivitiesBulkEditBarProps {
  /** Quantidade de atividades selecionadas */
  selectedCount: number;
  /** Indica se uma ação está em andamento (desabilita os botões) */
  loading?: boolean;
  /** Concluir as atividades selecionadas */
  onComplete: () => void;
  /** Reagendar (recebe ISO UTC) */
  onReschedule: (isoDateTime: string) => void;
  /** Atribuir responsável (apenas avulsas) */
  onAssign: (userId: string) => void;
  /** Pular passos de cadência selecionados */
  onSkip: () => void;
  /** Excluir atividades selecionadas (apenas avulsas; cadência/booking são ignoradas) */
  onDelete: () => void;
  /** v1.1.0 — Editar a atividade única selecionada (só aparece com selectedCount === 1) */
  onEdit?: () => void;
  /** v1.1.0 — Indica se a atividade única selecionada é editável (avulsa ou step manual em status não-final) */
  canEdit?: boolean;
  /** Sair do modo edição em massa */
  onCancel: () => void;
}

// ===========================================================================
// Componente principal
// ===========================================================================
export function ActivitiesBulkEditBar({
  selectedCount,
  loading,
  onComplete,
  onReschedule,
  onAssign,
  onSkip,
  onDelete,
  onEdit,
  canEdit,
  onCancel,
}: ActivitiesBulkEditBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-md mb-2 animate-in slide-in-from-top-2 duration-200">
      {/* Contador de seleção (mesmo padrão do DealBulkEditBar) */}
      <Badge variant="secondary" className="text-xs font-medium">
        {selectedCount} atividade(s) selecionada(s)
      </Badge>

      <div className="h-4 w-px bg-border" />

      {/* Ação: Concluir */}
      <Button
        size="sm"
        onClick={onComplete}
        disabled={loading}
        className="h-7 text-xs gap-1"
      >
        <CheckCircle2 className="h-3 w-3" />
        Concluir
      </Button>

      {/* Ação: Reagendar (Popover com datetime) */}
      <ReschedulePopover disabled={loading} onConfirm={onReschedule} />

      {/* Ação: Atribuir (Popover com seletor de usuário) */}
      <AssignPopover disabled={loading} onConfirm={onAssign} />

      {/* v1.1.0 — Ação: Editar (só com 1 selecionada, mesma regra do lápis da coluna Ações) */}
      {selectedCount === 1 && onEdit && (
        <Button
          size="sm"
          variant="outline"
          onClick={onEdit}
          disabled={loading || !canEdit}
          title={
            canEdit
              ? 'Editar atividade'
              : 'Somente atividades avulsas ou passos manuais em andamento podem ser editados'
          }
          className="h-7 text-xs gap-1"
        >
          <Pencil className="h-3 w-3" />
          Editar
        </Button>
      )}


      {/* Ação: Pular cadência */}
      <Button
        size="sm"
        variant="outline"
        onClick={onSkip}
        disabled={loading}
        className="h-7 text-xs gap-1"
      >
        <SkipForward className="h-3 w-3" />
        Pular (cadência)
      </Button>

      {/* Ação: Excluir (mesma regra da timeline do modal de negócio — RPC valida permissão) */}
      <Button
        size="sm"
        variant="outline"
        onClick={onDelete}
        disabled={loading}
        className="h-7 text-xs gap-1 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
      >
        <Trash2 className="h-3 w-3" />
        Excluir
      </Button>

      <div className="flex-1" />

      {/* Cancelar / sair do modo */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        disabled={loading}
        className="h-7 text-xs"
      >
        <X className="h-3 w-3 mr-1" />
        Cancelar
      </Button>
    </div>
  );
}

// ===========================================================================
// Popover: Reagendar em massa
// ===========================================================================
function ReschedulePopover({
  disabled,
  onConfirm,
}: {
  disabled?: boolean;
  onConfirm: (isoDateTime: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Padrão: agora + 1 dia (formato compatível com <input type="datetime-local">)
  const defaultLocal = (() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    d.setSeconds(0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [value, setValue] = useState(defaultLocal);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          className="h-7 text-xs gap-1"
        >
          <CalendarClock className="h-3 w-3" />
          Reagendar
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" align="end">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Nova data e hora
          </label>
          <Input
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-9"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!value}
            onClick={() => {
              const iso = new Date(value).toISOString();
              onConfirm(iso);
              setOpen(false);
            }}
          >
            Aplicar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ===========================================================================
// Popover: Atribuir responsável em massa (apenas avulsas)
// ===========================================================================
function AssignPopover({
  disabled,
  onConfirm,
}: {
  disabled?: boolean;
  onConfirm: (userId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          className="h-7 text-xs gap-1"
        >
          <UserPlus className="h-3 w-3" />
          Atribuir
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" align="end">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Responsável (apenas atividades avulsas)
          </label>
          <SystemUserSelector
            value={userId}
            onSelect={(id) => setUserId(id)}
            placeholder="Selecionar usuário..."
            allowClear={false}
          />
          <p className="text-[11px] text-muted-foreground italic">
            Passos de cadência herdam o vendedor do negócio e serão ignorados.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!userId}
            onClick={() => {
              if (userId) {
                onConfirm(userId);
                setOpen(false);
              }
            }}
          >
            Aplicar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
